import React, { useMemo } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { Line } from 'react-chartjs-2';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const STARTING_BALANCE = 100000;

function DrawdownChart({ trades }) {
  // Calculate drawdown data from trades
  const drawdownData = useMemo(() => {
    if (!trades || trades.length === 0) return { labels: [], values: [], maxDrawdown: 0 };

    // Filter to closed trades and sort by exit time
    const closedTrades = trades
      .filter(t => t.status === 'CLOSED' && t.exit_time && (t.pnl_usd ?? t.pnl_points) !== null)
      .sort((a, b) => new Date(a.exit_time) - new Date(b.exit_time));

    if (closedTrades.length === 0) return { labels: [], values: [], maxDrawdown: 0 };

    let balance = STARTING_BALANCE;
    let peak = STARTING_BALANCE;
    let maxDrawdown = 0;

    // Start with entry point at 0% drawdown
    const labels = [formatDate(closedTrades[0].entry_time)];
    const values = [0];

    for (const trade of closedTrades) {
      balance += (trade.pnl_usd ?? trade.pnl_points);

      // Update peak if we have a new high
      if (balance > peak) {
        peak = balance;
      }

      // Calculate drawdown as percentage from peak
      const drawdown = ((peak - balance) / peak) * 100;
      maxDrawdown = Math.max(maxDrawdown, drawdown);

      labels.push(formatDate(trade.exit_time));
      values.push(-drawdown); // Negative to show below zero line
    }

    return { labels, values, maxDrawdown };
  }, [trades]);

  if (!trades || trades.length === 0) {
    return (
      <div className="bg-dark-surface rounded-lg p-3 sm:p-6">
        <h3 className="text-base sm:text-lg font-semibold mb-3 sm:mb-4">Drawdown</h3>
        <div className="h-48 sm:h-64 flex items-center justify-center text-gray-400 text-sm">
          No trade data available
        </div>
      </div>
    );
  }

  const closedTradeCount = trades.filter(t => t.status === 'CLOSED').length;

  if (closedTradeCount === 0) {
    return (
      <div className="bg-dark-surface rounded-lg p-3 sm:p-6">
        <h3 className="text-base sm:text-lg font-semibold mb-3 sm:mb-4">Drawdown</h3>
        <div className="h-48 sm:h-64 flex items-center justify-center text-gray-400 text-sm">
          No closed trades yet
        </div>
      </div>
    );
  }

  const chartData = {
    labels: drawdownData.labels,
    datasets: [
      {
        label: 'Drawdown',
        data: drawdownData.values,
        borderColor: '#EF4444',
        backgroundColor: 'rgba(239, 68, 68, 0.2)',
        borderWidth: 2,
        fill: true,
        tension: 0.1,
        pointRadius: 2,
        pointHoverRadius: 5
      },
      {
        label: 'Zero Line',
        data: drawdownData.values.map(() => 0),
        borderColor: '#6B7280',
        borderWidth: 1,
        borderDash: [5, 5],
        pointRadius: 0,
        pointHoverRadius: 0,
        fill: false
      }
    ]
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index',
      intersect: false
    },
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        backgroundColor: '#1e2530',
        titleColor: '#e6edf3',
        bodyColor: '#e6edf3',
        borderColor: '#2d3748',
        borderWidth: 1,
        padding: 12,
        displayColors: false,
        callbacks: {
          label: (context) => {
            if (context.datasetIndex === 1) return null; // Hide zero line in tooltip
            const value = Math.abs(context.raw);
            return `Drawdown: ${value.toFixed(2)}%`;
          }
        },
        filter: (tooltipItem) => tooltipItem.datasetIndex === 0
      }
    },
    scales: {
      x: {
        grid: {
          color: '#334155',
          drawBorder: false
        },
        ticks: {
          color: '#9ca3af',
          maxRotation: 0,
          maxTicksLimit: 6
        }
      },
      y: {
        grid: {
          color: '#334155',
          drawBorder: false
        },
        ticks: {
          color: '#9ca3af',
          callback: (value) => `${value.toFixed(1)}%`
        },
        max: 0,
        suggestedMin: -Math.max(drawdownData.maxDrawdown * 1.1, 1) // Add 10% padding below max drawdown
      }
    }
  };

  return (
    <div className="bg-dark-surface rounded-lg p-3 sm:p-6">
      <div className="flex items-center justify-between mb-3 sm:mb-4">
        <h3 className="text-base sm:text-lg font-semibold">Drawdown</h3>
        <div className="text-sm text-gray-400">
          Max: <span className="text-accent-danger font-medium">{drawdownData.maxDrawdown.toFixed(2)}%</span>
        </div>
      </div>
      <div className="h-48 sm:h-64">
        <Line data={chartData} options={options} />
      </div>
    </div>
  );
}

function formatDate(isoString) {
  if (!isoString) return '';
  const date = new Date(isoString);
  return date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit'
  });
}

export default DrawdownChart;
